import './BasicInfo.scss';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import {ISelectItem} from '../../../../shared/types';
import {useForm} from 'react-hook-form';
import {
  ProductionBasicInfoFormData,
  productionBasicInfoSchema,
} from '../../../../schemas/productions/productionSchema';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  useGetProductionBasicInfo,
  useSaveProductionBasicInfo,
} from '../../../../hooks/productions/useProductions';
import {
  GetProductionBasicInfoRequestParam,
  ProductionTemplate,
} from '../../../../api/types/production.type';
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {
  ToastType,
  FormMode,
  ModuleType,
  FormAction,
  ProductionStatus,
} from '../CreateNewProductionUtils';
import {setIsFieldsChanged} from '../../../../redux/slice/productionSlice';
import {useDispatch} from 'react-redux';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface BasicInfoProps {
  securityGroupId: number;
  productionSetId: number;
  productionId: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  mode?: FormMode;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

const BasicInfo = forwardRef<StepChildRef, BasicInfoProps>(
  (
    {
      securityGroupId = 0,
      productionSetId = 0,
      productionId = 0,
      isProductionSet = true,
      getStatus,
      goNext,
      mode = FormMode.CREATE,
      onStateChange,
      productionStatusCode = null,
      isUpdateNeeded = false,
      onUpdateNeededChange,
    },
    ref
  ) => {
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [selectedProductionTemplate, setSelectedProductionTemplate] =
      useState<ISelectItem | null>(null);
    const [selectedProduction, setSelectedProduction] =
      useState<ISelectItem | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    const {data: productionBasicInfo} = useGetProductionBasicInfo({
      securityGroupId: securityGroupId,
      moduleType: moduleType,
      parentSetId: productionSetId,
      productionId: productionId,
    } as GetProductionBasicInfoRequestParam);

    const {mutate: saveProductionBasicInfo} = useSaveProductionBasicInfo();

    const {
      register,
      handleSubmit,
      setValue,
      formState: {errors, isSubmitting, isDirty, isValid},
      reset,
      watch,
      trigger,
    } = useForm<ProductionBasicInfoFormData>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: zodResolver(productionBasicInfoSchema()),
      defaultValues: {
        productionName: '',
        productionTemplateId: 0,
        productionDescription: '',
        exportPriorProductionVersions: false,
        exportProductionId: 0,
        action: FormAction.SAVE,
      },
    });

    const productionTemplateList: ISelectItem[] = useMemo(
      () => [
        // Add default "Select Template" option
        {
          id: 0,
          name: t(
            'productionsAndExports.createNewProduction.basicInfo.selectTemplate'
          ),
          isDefault: false,
        },
        // Map existing templates
        ...(productionBasicInfo?.productionTemplateList || []).map(
          (item: ProductionTemplate) => ({
            id: item.templateID,
            name: item.templateName,
            isDefault: item.isDefault,
          })
        ),
      ],
      [productionBasicInfo?.productionTemplateList]
    );

    // Map production set list for dropdown
    const productionList: ISelectItem[] = useMemo(
      () => [
        // Add default "Select" option
        {
          id: 0,
          name: t(
            'productionsAndExports.createNewProduction.basicInfo.selectProduction'
          ),
          isDefault: false,
        },
        // Map production sets from API response
        ...(productionBasicInfo?.productionSetList || []).map((item) => ({
          id: Number(item.value),
          name: item.text,
          isDefault: false,
        })),
      ],
      [productionBasicInfo?.productionSetList]
    );

    useEffect(() => {
      if (
        productionBasicInfo &&
        productionBasicInfo?.productionTemplateList?.length >= 0
      ) {
        if (mode === FormMode.CREATE && productionId === 0) {
          // Create mode: Use default template logic
          // Don't validate on initial load - let validation happen naturally as user types
          const defaultTemplate = productionTemplateList.find(
            (item) => item.isDefault
          );

          if (defaultTemplate) {
            // If default template found, use it
            setValue('productionTemplateId', Number(defaultTemplate.id), {
              shouldDirty: false, // Don't mark as dirty on initial load
              shouldValidate: false, // Don't validate on initial load
            });
            setSelectedProductionTemplate(defaultTemplate);
          } else {
            // If no default template found, use the "Select Template" option (id: 0)
            setValue('productionTemplateId', 0, {
              shouldDirty: false, // Don't mark as dirty on initial load
              shouldValidate: false, // Don't validate on initial load
            });
            setSelectedProductionTemplate(productionTemplateList[0]); // First item is "Select Template"
          }
        } else if (mode !== FormMode.CREATE && productionId > 0) {
          // Edit mode: Use existing logic
          reset(
            {
              productionName: productionBasicInfo?.productionName || '',
              productionTemplateId:
                productionBasicInfo?.productionTemplateID || 0,
              productionDescription:
                productionBasicInfo?.productionDescription || '',
              exportPriorProductionVersions: !!Number(
                productionBasicInfo?.exportProductionId
              ),
              exportProductionId: productionBasicInfo?.exportProductionId
                ? Number(productionBasicInfo.exportProductionId)
                : 0,
              action: FormAction.SAVE,
            },
            {
              keepDefaultValues: false,
              keepDirty: false,
              keepIsValid: false, // Changed to false to allow validation to run fresh
            }
          );

          setSelectedProductionTemplate(
            productionTemplateList.find(
              (item) => item.id === productionBasicInfo?.productionTemplateID
            ) || null
          );

          setSelectedProduction(
            productionList.find(
              (item) =>
                item.id === Number(productionBasicInfo?.exportProductionId)
            ) || null
          );

          setIsSaved(true);
          // Trigger validation after reset to ensure isValid is updated
          // Use setTimeout to ensure validation runs after form state is fully updated
          setTimeout(() => {
            trigger();
          }, 0);
        }
      }
    }, [
      productionBasicInfo,
      mode,
      productionId,
      reset,
      setValue,
      productionTemplateList,
      trigger,
      productionList,
    ]);

    // Set selected production when production list is loaded and exportProductionId exists
    useEffect(() => {
      if (
        productionList.length > 0 &&
        productionBasicInfo?.exportProductionId &&
        !selectedProduction
      ) {
        const productionId = Number(productionBasicInfo.exportProductionId);
        const foundProduction = productionList.find(
          (item) => item.id === productionId
        );
        if (foundProduction) {
          setSelectedProduction(foundProduction);
        }
      }
    }, [
      productionList,
      productionBasicInfo?.exportProductionId,
      selectedProduction,
    ]);

    const isSaveDisabled =
      isSubmitting ||
      !isValid ||
      mode === FormMode.VIEW ||
      (!isDirty && isSaved);

    useEffect(() => {
      if (isDirty) {
        dispatch(setIsFieldsChanged({isFieldsChanged: true}));
      }
    }, [isDirty]);

    const isNextDisabled = isSubmitting || !isValid;

    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS;

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onSubmit)();
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }
        setValue('action', FormAction.NEXT);
        handleSubmit(onSubmit)();
      },
    }));

    // Report state changes to parent
    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    const onSubmit = (data: ProductionBasicInfoFormData) => {
      // For exports, use the selected production ID from dropdown, otherwise 0
      const exportProductionIdValue =
        !isProductionSet && data.exportProductionId
          ? data.exportProductionId
          : 0;

      // Helper function to check if we should navigate
      const shouldNavigate = () => {
        const targetPath = `/Production/Home/Edit`;
        const currentPath = location.pathname;

        // Check if current pathname already matches the target path
        return currentPath !== targetPath;
      };

      if (data.action === FormAction.SAVE) {
        saveProductionBasicInfo(
          {
            productionName: data.productionName,
            productionTemplateID:
              data.productionTemplateId === 0
                ? undefined
                : data.productionTemplateId,
            productionDescription: data.productionDescription,
            parentSecurityGroupId: securityGroupId,
            parentProductionSetID: productionSetId,
            productionID: productionId,
            disclaimer: '',
            isMarkComplete: false,
            isProductionSet: isProductionSet,
            exportProductionId: exportProductionIdValue,
            isExportProduction: !isProductionSet,
          },
          {
            onSuccess: (savedData) => {
              setIsSaved(true);
              // Notify parent to show success toast
              getStatus(true, ToastType.Success, savedData?.message);
              reset(data);
              // Only navigate if not already on the edit page
              if (shouldNavigate()) {
                navigate(
                  `/Production/Home/Edit?productionId=${savedData.productionID}`
                );
              }
              // Update parent state to track that update is needed after save
              onUpdateNeededChange?.(true);
            },
            onError: (err) => {
              // Notify parent to show error toast
              getStatus(true, ToastType.Error, err?.message);
            },
          }
        );
      }

      if (data.action === FormAction.NEXT) {
        if (!isUpdateNeeded) {
          goNext?.();
          return;
        }
        saveProductionBasicInfo(
          {
            productionName: data.productionName,
            productionTemplateID:
              data.productionTemplateId === 0
                ? undefined
                : data.productionTemplateId,
            productionDescription: data.productionDescription,
            parentSecurityGroupId: securityGroupId,
            parentProductionSetID: productionSetId,
            productionID: productionId,
            disclaimer: '',
            isMarkComplete: true,
            isProductionSet: isProductionSet,
            exportProductionId: exportProductionIdValue,
            isExportProduction: !isProductionSet,
          },
          {
            onSuccess: (savedData) => {
              getStatus(
                true,
                ToastType.Success,
                t(
                  'productionsAndExports.createNewProduction.common.nextSuccessToast'
                )
              );

              goNext?.();
              // Only navigate if not already on the edit page
              if (shouldNavigate()) {
                navigate(
                  `/Production/Home/Edit?productionId=${savedData.productionID}`
                );
              }
            },
            onError: (err) => {
              // Notify parent to show error toast
              getStatus(true, ToastType.Error, err?.message);
            },
          }
        );
      }
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    };

    return (
      <Box
        className="new-production-form-section"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        role="form"
        aria-label={t(
          'productionsAndExports.createNewProduction.basicInfo.headerText'
        )}
      >
        <Box className="new-production-form basic-info">
          <Box className="new-production-form-header">
            <Typography
              id="basic-info-header"
              className="typo-text subhead2_size"
              data-testid={`basic-info-header-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.basicInfo.headerText'
              )}
            </Typography>
          </Box>
          <Box
            className="new-production-form-body"
            role="group"
            aria-labelledby="basic-info-header"
          >
            <Box className="input-group">
              <Box className="input-item">
                <Typography
                  id="production-name-label"
                  className="typo-text label2_size"
                  data-testid={`basic-info-production-name-title`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.basicInfo.productionNameTitle'
                  )}{' '}
                  <span data-testid={`basic-info-asterics`}>*</span>
                </Typography>
                <ArrTextField
                  id="productionName"
                  dataTestId="basic-info-production-name"
                  placeholder={t(
                    'productionsAndExports.createNewProduction.basicInfo.productionNameLabel'
                  )}
                  {...register('productionName')}
                  value={watch('productionName')}
                  showErrorIcon={!!errors.productionName}
                  state={
                    mode === FormMode.VIEW || isInProgressOrCompleted
                      ? 'inactive'
                      : errors.productionName
                        ? 'error'
                        : 'enabled'
                  }
                  supportText={errors.productionName?.message || ''}
                  sizeVariant="medium"
                  slotProps={{
                    input: {
                      'aria-label': t(
                        'productionsAndExports.createNewProduction.basicInfo.productionNameLabel'
                      ),
                      'aria-labelledby': 'production-name-label',
                      'aria-required': 'true',
                      'aria-invalid': !!errors.productionName,
                    },
                  }}
                />
              </Box>
              <Box className="input-item">
                <Typography
                  id="production-template-label"
                  className="typo-text label2_size"
                  data-testid={`basic-info-production-template-title`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.basicInfo.productionTemplateTitle'
                  )}
                </Typography>
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'productionsAndExports.createNewProduction.basicInfo.productionTemplateLabel'
                  )}
                  dropdownItems={productionTemplateList}
                  onSelectionChange={(item) => {
                    setValue('productionTemplateId', Number(item?.id), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setSelectedProductionTemplate(item);
                    // Trigger validation to ensure isValid is updated
                    trigger('productionTemplateId');
                  }}
                  selectedValues={selectedProductionTemplate}
                  searchable
                  error={!!errors.productionTemplateId}
                  errorText={errors.productionTemplateId?.message || ''}
                  disabled={mode == 'view' || isInProgressOrCompleted}
                  sizeVariant="medium"
                  selectRestProps={{
                    'aria-label': t(
                      'productionsAndExports.createNewProduction.basicInfo.productionTemplateAriaLabel'
                    ),
                    'aria-labelledby': 'production-template-label',
                    'aria-invalid': !!errors.productionTemplateId,
                  }}
                  dataTestId="asd-production-template-label"
                />
              </Box>
            </Box>
            <Box className="input-group">
              <Box className="input-item">
                <Typography
                  id="production-description-label"
                  className="typo-text label2_size"
                  data-testid={`basic-info-production-description-title`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.basicInfo.productionDescriptionTitle'
                  )}
                </Typography>
                <ArrTextField
                  id="description"
                  placeholder={t(
                    'productionsAndExports.createNewProduction.basicInfo.productionDescriptionLabel'
                  )}
                  dataTestId="basic-info-error-text-input"
                  multiline
                  maxRows={4}
                  fullWidth
                  {...register('productionDescription')}
                  value={watch('productionDescription')}
                  showErrorIcon={!!errors.productionDescription}
                  state={
                    mode === FormMode.VIEW || isInProgressOrCompleted
                      ? 'inactive'
                      : errors.productionDescription
                        ? 'error'
                        : 'enabled'
                  }
                  supportText={errors.productionDescription?.message || ''}
                  slotProps={{
                    input: {
                      'aria-label': t(
                        'productionsAndExports.createNewProduction.basicInfo.productionDescriptionLabel'
                      ),
                      'aria-labelledby': 'production-description-label',
                      'aria-invalid': !!errors.productionDescription,
                      'aria-multiline': 'true',
                      style: {
                        resize: isInProgressOrCompleted ? 'none' : 'vertical', // disable resize conditionally
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            {/* Export Prior Production Versions Toggle and Dropdown - Only visible for export sets */}
            {!isProductionSet && (
              <Box className="input-group">
                <Box className="input-item">
                  <Box
                    className="export-prior-production-container"
                    display="flex"
                    alignItems="center"
                  >
                    <Box className="export-toggle-area">
                      <ArrToggle
                        value={watch('exportPriorProductionVersions')}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setValue('exportPriorProductionVersions', checked, {
                            shouldDirty: true,
                            shouldValidate: false, // Don't validate on toggle change
                          });
                          if (!checked) {
                            // When turning OFF: Clear selection and validate to remove error
                            setValue('exportProductionId', 0, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            setSelectedProduction(null);
                            // Trigger validation to clear any errors
                            trigger('exportProductionId');
                          } else {
                            // When turning ON: Set value but don't validate yet
                            // Validation will happen when user selects a production
                            const existingProductionId = Number(
                              productionBasicInfo?.exportProductionId || 0
                            );
                            setValue(
                              'exportProductionId',
                              existingProductionId,
                              {
                                shouldDirty: true,
                                shouldValidate: false, // Don't validate until user selects
                              }
                            );
                            // Only validate if there's already a valid production ID
                            if (existingProductionId > 0) {
                              trigger('exportProductionId');
                            }
                          }
                        }}
                        disabled={mode === 'view' || isInProgressOrCompleted}
                        toggleSize="medium"
                        ariaLabel={t(
                          'productionsAndExports.createNewProduction.basicInfo.exportPriorProductionVersions'
                        )}
                        dataTestId="export-prior-production-versions-toggle"
                      />
                      <Typography
                        id="export-prior-production-versions-label"
                        className="typo-text subhead5_size export-prior-toggle-label"
                        data-testid={`basic-info-export-prior-production-versions`}
                      >
                        {t(
                          'productionsAndExports.createNewProduction.basicInfo.exportPriorProductionVersions'
                        )}
                      </Typography>
                    </Box>

                    {watch('exportPriorProductionVersions') && (
                      <Box className="export-prior-production-dropdown-wrapper">
                        <Typography
                          id="select-production-label"
                          className="typo-text label2_size"
                          data-testid={`basic-info-select-production-title`}
                        >
                          {t(
                            'productionsAndExports.createNewProduction.basicInfo.selectProductionTitle'
                          )}
                        </Typography>
                        <Box className="export-prior-production-dropdown">
                          <ArrSingleSelectDropdown
                            dropdownLabel={t(
                              'productionsAndExports.createNewProduction.basicInfo.selectProductionLabel'
                            )}
                            dropdownItems={productionList}
                            onSelectionChange={(item) => {
                              const productionId = item?.id
                                ? Number(item.id)
                                : 0;
                              setValue('exportProductionId', productionId, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              setSelectedProduction(item);
                              // Trigger validation to ensure isValid is updated
                              trigger('exportProductionId');
                            }}
                            selectedValues={selectedProduction}
                            searchable
                            error={!!errors.exportProductionId}
                            errorText={errors.exportProductionId?.message || ''}
                            disabled={
                              mode === 'view' || isInProgressOrCompleted
                            }
                            sizeVariant="medium"
                            selectRestProps={{
                              'aria-label': t(
                                'productionsAndExports.createNewProduction.basicInfo.selectProductionAriaLabel'
                              ),
                              'aria-labelledby': 'select-production-label',
                              'aria-invalid': !!errors.exportProductionId,
                            }}
                            dataTestId="asd-select-production-label"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);

BasicInfo.displayName = 'BasicInfo';

export default BasicInfo;
